import { Capacitor } from '@capacitor/core'
import { GGCamera, hasPlugin, legacyPost, type ShareApp } from '@/lib/nativeCamera'

/**
 * Saving a take and handing it to an app (Brian's ggshare bridge, Oct 22 2026;
 * docs/NATIVE-CAMERA-CONTRACT.md).
 *
 *   saveVideo   the phone: into Photos (add-only), where TikTok, Instagram and
 *               YouTube pick videos up; the web: a download.
 *   openApp     the phone: the caption on the clipboard, then the app by its
 *               URL scheme, or its App Store page; the web: the upload page.
 *   installedApps  which of the three are on the phone (null on the web).
 */

export const SHARE_APPS: Record<ShareApp, { name: string; web: string }> = {
  instagram: { name: 'Instagram', web: 'https://www.instagram.com/' },
  tiktok: { name: 'TikTok', web: 'https://www.tiktok.com/upload' },
  youtube: { name: 'YouTube Shorts', web: 'https://studio.youtube.com/' },
}

const legacyShare = () => {
  try { return !!(window.__ggApps && window.webkit?.messageHandlers?.ggshare) } catch { return false }
}

/** Native means the take can go straight into Photos. */
export const canSaveToPhotos = () => hasPlugin() || legacyShare()

export async function installedApps(): Promise<Partial<Record<ShareApp, boolean>> | null> {
  if (hasPlugin()) {
    try { return await GGCamera.installedApps() } catch { return null }
  }
  if (legacyShare()) return window.__ggApps ?? null
  return null
}

export async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea')
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0'
    document.body.appendChild(ta); ta.select()
    const ok = document.execCommand('copy')
    ta.remove()
    return ok
  } catch { return false }
}

const blobToBase64 = (blob: Blob) => new Promise<string>((ok, no) => {
  const fr = new FileReader()
  fr.onload = () => ok(String(fr.result).split(',')[1] ?? '')
  fr.onerror = () => no(fr.error)
  fr.readAsDataURL(blob)
})

export type SaveResult = 'photos' | 'downloaded' | 'shared' | 'failed'

/**
 * Saves a take. Copies `caption` too (the main button is "Save video + copy
 * caption"). Call it inside the tap: the clipboard and the download need it.
 */
export async function saveVideo(blob: Blob, opts: { name: string; caption?: string }): Promise<SaveResult> {
  const { name, caption } = opts
  if (caption) void copyText(caption)

  if (hasPlugin()) {
    try {
      const data = await blobToBase64(blob)
      const r = await GGCamera.saveVideo({ data, name, type: blob.type || 'video/mp4', caption })
      return r?.saved ? 'photos' : 'failed'
    } catch { return 'failed' }
  }

  if (legacyShare()) {
    return new Promise<SaveResult>((resolve) => {
      const done = (ok: boolean) => { window.__ggSaved = undefined; resolve(ok ? 'photos' : 'failed') }
      window.__ggSaved = done
      blobToBase64(blob)
        .then((data) => legacyPost('ggshare', { cmd: 'save', name, type: blob.type, data, caption }))
        .catch(() => done(false))
      setTimeout(() => { if (window.__ggSaved === done) done(false) }, 60_000)
    })
  }

  // In the app without the plugin, an anchor download goes nowhere (WKWebView):
  // the share sheet's Save Video is the way into Photos.
  if (Capacitor.isNativePlatform()) {
    const file = new File([blob], name, { type: blob.type || 'video/mp4' })
    try {
      if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file] }); return 'shared' }
    } catch { return 'failed' }
  }

  try {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = name; a.rel = 'noopener'
    document.body.appendChild(a); a.click(); a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 30_000)
    return 'downloaded'
  } catch { return 'failed' }
}

export type OpenResult = 'app' | 'store' | 'shared' | 'web' | 'failed'

/**
 * Hands a take to an app: its caption on the clipboard, then the app (or its
 * App Store page). In a browser, the platform's upload page in a new tab
 * (Brian's record.html, Oct 22).
 */
export async function openApp(app: ShareApp, caption: string): Promise<OpenResult> {
  void copyText(caption)
  if (hasPlugin()) {
    try { return (await GGCamera.openApp({ app, caption })).opened } catch { return 'failed' }
  }
  if (legacyShare()) {
    legacyPost('ggshare', { cmd: 'open', app, caption })
    return window.__ggApps?.[app] ? 'app' : 'store'
  }
  window.open(SHARE_APPS[app].web, '_blank', 'noopener')
  return 'web'
}

export const nativeMedia = { saveVideo, openApp, installedApps, copyText, canSaveToPhotos, SHARE_APPS }
